// Turning the link an admin pasted into something the page can play.
//
// The admin panel asks for one thing, a YouTube or Vimeo URL, because that is
// what an admin actually has. Everything else (which provider, the video id,
// the embed URL, the still image) is derived here rather than stored, so the
// database holds one fact and there is no second copy to drift.
//
// The host allowlist is the same set three times over, on purpose:
//   1. this parser,
//   2. the `testimonials_video_host` check constraint in the migration,
//   3. the site's Content-Security-Policy `frame-src`.
// These rows end up in an <iframe src> on a public page, so a value that is
// not a real video URL must never reach the DOM. Any one layer failing still
// leaves two.

import { extractVimeoId, vimeoEmbedUrl, vimeoOembedUrl, vimeoWatchUrl } from "@/lib/vimeo";

export type TestimonialVideo = {
  provider: "youtube" | "vimeo";
  id: string;
  /** For an <iframe src>. Always on a host the CSP frame-src allows. */
  embedUrl: string;
  /** Opens the video on the provider's own site. */
  watchUrl: string;
  /**
   * Still image to show before the viewer clicks play, when the provider has
   * one at a predictable URL. YouTube does. Vimeo does not, so for those see
   * `thumbnailLookupUrl`.
   */
  posterUrl: string | null;
  /** Lower-resolution poster, for when `posterUrl` 404s. */
  posterFallbackUrl: string | null;
  /**
   * For providers whose still image has no predictable URL (Vimeo): an API
   * address that answers with it. Fetching it is a request to a third party,
   * so the card only does so when nothing better (the admin's own image) has
   * worked. Null when `posterUrl` already says everything.
   */
  thumbnailLookupUrl: string | null;
};

/** YouTube ids are exactly 11 characters of URL-safe base64. */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
]);

/** Path prefixes that carry the id in the next segment. */
const YOUTUBE_PATH_PREFIXES = ["embed", "shorts", "live", "v"];

const parseHttpsUrl = (raw: string | null | undefined): URL | null => {
  if (!raw) return null;
  const value = raw.trim();
  if (!value) return null;
  try {
    const url = new URL(value);
    // http:// is rejected rather than upgraded: an admin pasting a plain-http
    // link has pasted something the browser would block anyway.
    return url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
};

const extractYouTubeId = (url: URL): string | null => {
  const host = url.hostname.toLowerCase();

  // youtu.be/<id>
  if (host === "youtu.be" || host === "www.youtu.be") {
    const id = url.pathname.split("/").filter(Boolean)[0] ?? "";
    return YOUTUBE_ID.test(id) ? id : null;
  }

  if (!YOUTUBE_HOSTS.has(host)) return null;

  // /watch?v=<id>
  const v = url.searchParams.get("v");
  if (v && YOUTUBE_ID.test(v)) return v;

  // /embed/<id>, /shorts/<id>, /live/<id>, /v/<id>
  const parts = url.pathname.split("/").filter(Boolean);
  if (parts.length >= 2 && YOUTUBE_PATH_PREFIXES.includes(parts[0].toLowerCase())) {
    return YOUTUBE_ID.test(parts[1]) ? parts[1] : null;
  }

  return null;
};

/**
 * The privacy-enhanced YouTube host, which the CSP already allows. `rel=0`
 * keeps the end-of-video suggestions to the same channel instead of sending
 * the viewer somewhere else from a testimonial.
 */
const youtubeEmbedUrl = (id: string): string =>
  `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`;

/**
 * Resolve a pasted link into a playable video, or null if it is not one.
 *
 * Null is the "do not render" signal: the public grid skips such a row rather
 * than putting an unknown URL in an iframe.
 */
export const resolveTestimonialVideo = (
  raw: string | null | undefined,
): TestimonialVideo | null => {
  const url = parseHttpsUrl(raw);
  if (!url) return null;

  const youtubeId = extractYouTubeId(url);
  if (youtubeId) {
    return {
      provider: "youtube",
      id: youtubeId,
      embedUrl: youtubeEmbedUrl(youtubeId),
      watchUrl: `https://www.youtube.com/watch?v=${youtubeId}`,
      // maxres is 16:9 and crisp but is not generated for every video, so the
      // component falls back to hqdefault, which always exists.
      posterUrl: `https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`,
      posterFallbackUrl: `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`,
      thumbnailLookupUrl: null,
    };
  }

  const vimeo = extractVimeoId(url.toString());
  if (vimeo) {
    return {
      provider: "vimeo",
      id: vimeo.id,
      embedUrl: vimeoEmbedUrl(vimeo.id, vimeo.hash),
      watchUrl: vimeoWatchUrl(vimeo.id, vimeo.hash),
      posterUrl: null,
      posterFallbackUrl: null,
      thumbnailLookupUrl: vimeoOembedUrl(vimeo.id, vimeo.hash),
    };
  }

  return null;
};

/** True when the link would play. Used by the admin form before saving. */
export const isPlayableTestimonialUrl = (raw: string | null | undefined): boolean =>
  resolveTestimonialVideo(raw) !== null;

/**
 * Why a link was rejected, phrased for the admin who pasted it.
 *
 * A generic "invalid URL" makes an admin retype the same link; naming the
 * actual problem ("that is a channel page, not a video") does not.
 */
export const describeTestimonialUrlProblem = (raw: string | null | undefined): string | null => {
  const value = (raw ?? "").trim();
  if (!value) return "Paste the YouTube or Vimeo link for the video.";
  if (resolveTestimonialVideo(value)) return null;

  let url: URL | null = null;
  try {
    url = new URL(value);
  } catch {
    return "That does not look like a link. Copy the full address from the browser bar, starting with https://.";
  }

  if (url.protocol === "http:") {
    return "Use the https:// version of that link.";
  }
  if (url.protocol !== "https:") {
    return "Only https:// links to YouTube or Vimeo can be used.";
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  if (host === "youtube.com" || host === "youtu.be" || host === "m.youtube.com") {
    return "That is a YouTube link but there is no video id in it. Open the video itself and copy the link from the browser bar, or use the Share button.";
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    return "That is a Vimeo link but there is no video id in it. Open the video itself and copy the link from the browser bar.";
  }
  return "Only YouTube and Vimeo videos can be embedded. Upload the video to one of those, then paste the link here.";
};

/**
 * Ordering for the public list: `sort_order` first, then oldest first so the
 * order is total even when an admin leaves several rows at the default 0.
 */
export const compareTestimonials = <T extends { sort_order: number; created_at: string }>(
  a: T,
  b: T,
): number => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at);
